import React, { useEffect, useRef } from 'react';
import './About.css';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Slash } from 'lucide-react';
import '../Breadcrumb/ArtisanBreadcrumb.css';
import About1 from './About7.png';
import About2 from './About2.png';
import About3 from './About3.png';
import About4 from './About4.png';
import About5 from './About5.png';
import SEO from '../SEO';
const sections = [
  {
    id: 1,
    title: "Fresh Roots, Bold Vision",
    subtitle: "EST. 2012",
    text: "We are a young & fresh company bringing different designs to the market since 2012. Combining curiosity and craft, the studio explores forms, textures, and finishes that feel both familiar and on-trend, turning everyday spaces into living stories.",
    image: About1, 
    alt: "Designer sketching furniture",
    layout: "about-page-layout-text-left" 
  },
  {
    id: 2,
    title: "Tradition Meets Now",
    subtitle: "HERITAGE & CRAFT",
    text: "Deeply inspired by old traditional crafts, which are still abundant and alive in our region. Combining tradition with the new modern & on-trend designs allows such collections to be familiar yet forward—where artisan techniques meet contemporary silhouettes and functionality.",
    image: About2, 
    alt: "Jodhpur Architecture Stone",
    layout: "about-page-layout-image-left" 
  },
  {
    id: 3,
    title: "Desert Spirit, Lasting Value",
    subtitle: "RESILIENT DESIGN",
    text: "Stand-out interior products give more value to you and your customer. In us lives the spirit of the desert. That spirit—resilient, minimal, and quietly powerful—guides the way we make: with integrity, longevity, and a calm aesthetic that elevates every space.",
    image: About3, 
    alt: "Minimalist chair in desert setting",
    layout: "about-page-layout-text-left"
  },
  {
    id: 4,
    title: "Simplicity in Design",
    subtitle: "PURE & HONEST",
    text: "No shiny hardware. No senseless details. Just very good interior products, made honestly. The design language is pared-back and purposeful, prioritizing clean lines, honesty of material, and everyday usability. Natural finishes and tactile textures lead the experience.",
    image: About4, 
    alt: "Simple wooden table design",
    layout: "about-page-layout-image-left"
  },
  {
    id: 5,
    title: "Environment & Sustainability",
    subtitle: "CONSCIOUS LIVING",
    text: "We take this seriously. Our commitment starts by being a registered FSC certified company. Our wood is thoroughly minimalized and chosen with recyclability in mind to lower impact. We create reclaimed wood products that have a past, a story, and a special feel.",
    image: About5, 
    alt: "Green leaf nature concept",
    layout: "about-page-layout-text-left"
  }
];
const ArtisanBreadcrumb = ({ 
  title = "About Us", 
  bgImage, 
  paths = [] 
}) => {

  // Default paths if none provided
  const breadcrumbs = paths.length > 0 ? paths : [
    { name: "Home", link: "/" },
    { name: "About Us", link: "/about" }
  ];

  return (
    <div className="bc-art-wrapper">
      
      {/* 1. DYNAMIC BACKGROUND IMAGE (Parallax feel) */}
      <div 
        className="bc-art-bg"
        style={{ 
          backgroundImage: `url(${bgImage || ''})` 
        }}
      >
        <div className="bc-art-overlay"></div>
      </div>

      {/* 2. ANIMATED DECORATION RING (Subtle rotation) */}
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        className="bc-art-deco-ring"
      >
        <svg viewBox="0 0 100 100">
           <path d="M50 0C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0zm0 90C27.9 90 10 72.1 10 50S27.9 10 50 10s40 17.9 40 40-17.9 40-40 40z" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" strokeDasharray="4 4"/>
        </svg>
      </motion.div>

      {/* 3. CONTENT CONTAINER */}
      <div className="bc-art-content">
        
        {/* Animated Title */}
        <motion.h1 
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bc-art-title"
        >
          {title}
        </motion.h1>

        {/* The Glassmorphic Breadcrumb Bar */}
        <motion.nav 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bc-art-bar"
        >
          <ul className="bc-art-list">
            
            {/* Home Icon Always First */}
            <li>
              <a href="/" className="bc-art-home-icon">
                <Home size={16} />
              </a>
            </li>

            {/* Loop through paths */}
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;

              return (
                <li key={index} className="bc-art-item">
                  
                  {/* Separator */}
                  <span className="bc-art-sep">
                    <ChevronRight size={12} strokeWidth={3} />
                  </span>

                  {/* Link or Active Text */}
                  {isLast ? (
                    <span className="bc-art-text active">
                      {crumb.name}
                    </span>
                  ) : (
                    <a href={crumb.link} className="bc-art-text link">
                      {crumb.name}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </motion.nav>
      </div>
    </div>
  );
};
const AboutSection = ({ data }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Add class when element enters viewport
          if (entry.isIntersecting) {
            entry.target.classList.add('about-page-animate-active');
          }
        });
      },
      { 
        threshold: 0.25, // Trigger when 25% of the item is visible
        rootMargin: "0px"
      } 
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  return (
   
    <div 
      ref={sectionRef} 
      className={`about-page-section ${data.layout}`}
    >
      {/* Content Side */}
      <div className="about-page-content-wrapper">
        <div className="about-page-text-inner">
          <span className="about-page-subtitle">{data.subtitle}</span>
          <h2 className="about-page-title">{data.title}</h2>
          <div className="about-page-divider"></div>
          <p className="about-page-desc">{data.text}</p>
        </div>
      </div>

      {/* Image Side */}
      <div className="about-page-image-wrapper">
        <div className="about-page-image-scale-container">
          <img src={data.image} alt={data.alt} className="about-page-img" />
        </div>
      </div>
    </div>
  );
};

export default function AboutPage() {
  return (
     <>
    <SEO 
  title="Our Heritage & Artisan Story | Sohani Handicrafts Jodhpur"
  description="Discover the legacy of Sohani Handicrafts. Our journey from a small workshop in Jodhpur to a global exporter of sustainable, hand-carved wooden art and heritage furniture."
  keywords="artisan heritage, traditional woodworking india, history of jodhpur handicrafts, handmade furniture legacy, master craftsmen rajasthan, sustainable wood sourcing india"
  url="/about"
/>
    <ArtisanBreadcrumb/>
    <div className="about-page-container">
      {sections.map((section) => (
        <AboutSection key={section.id} data={section} />
      ))}
    </div>
    </>
  );
}